/** `CLensFlare` and `CFlare`: the sun's flare a map's environment record turns
 *  on (`EterLib/LensFlare.cpp`, `LensFlare.h:39-125`).
 *
 * `CMapOutdoor` owns one and drives it twice a frame: `RenderBeforeLensFlare`
 * draws the main flare and then recomputes where the sun is, and
 * `RenderAfterLensFlare` adjusts the brightness and draws the wash and the
 * eight pieces (`MapOutdoorRender.cpp:190-208`). `SetEnvironmentLensFlare`
 * characterises it from the record and only then loads the textures
 * (`MapOutdoor.cpp:325-340`), which is why nothing here fetches until a record
 * says `Enable 1` -- and every one of the 42 shipped records says `Enable 0`.
 */

import { createVertexArray } from "./gl/buffer";
import { createProgram, uniformLocations, type Uniforms } from "./gl/shader";
import { decodeImage, type RgbaImage } from "./image";
import type { MapEnvironment } from "./mapassets";
import type { Mat4 } from "./view";

/** Where `tools/build_environment.mjs` puts the flare sprites. */
const ENVIRONMENT_DIR = "environment/";

/** `c_fHalfMaxBright` (`LensFlare.h:40`). */
export const HALF_MAX_BRIGHT = 0.45;
/** `c_fDistanceScale` (`LensFlare.h:41`). */
export const DISTANCE_SCALE = 1.0;
/** `c_nDepthTestDimension` (`LensFlare.h:42`): the side of the square of depth
 *  samples `AdjustBrightness` counts changes in. */
export const DEPTH_TEST_DIMENSION = 15;

/** How far along the light's own direction the sun is put before it is
 *  projected (`LensFlare.cpp:133-135`). */
export const SUN_DISTANCE = 99999999;

export interface FlarePiece {
  /** `g_strFiles` (`LensFlare.cpp:39-50`), lowercased for the URL. */
  readonly texture: string;
  /** `g_fPosition` (`:51-61`): -1 is the light, 0 the screen centre, 1 the far
   *  end of the line through it. */
  readonly position: number;
  /** `g_fWidth` (`:62-72`). Used as a half-extent in both axes
   *  (`CFlare::Draw`'s `fCenterX ± fW`, `:569-593`), so a "width" of 20 is a
   *  40-pixel square. */
  readonly width: number;
  /** `g_afColors` (`:74-84`), r/g/b/a. */
  readonly colour: readonly [number, number, number, number];
}

/** The eight pieces `CFlare::Init` builds, in its own order (`:509-530`). */
export const FLARE_PIECES: readonly FlarePiece[] = [
  { texture: "flare2.dds", position: -0.55, width: 20, colour: [1, 1, 0, 1] },
  { texture: "flare1.dds", position: -0.5, width: 32, colour: [1, 1, 1, 1] },
  { texture: "flare2.dds", position: -0.45, width: 20, colour: [0, 1, 0, 0.8] },
  { texture: "flare1.dds", position: 0.2, width: 32, colour: [0.3, 0.5, 1, 0.9] },
  { texture: "flare6.dds", position: 0.3, width: 100, colour: [0.3, 0.5, 1, 0.6] },
  { texture: "flare4.dds", position: 0.95, width: 32, colour: [1, 0.6, 0.9, 0.4] },
  { texture: "flare2.dds", position: 0.9, width: 20, colour: [1, 0, 0, 0.5] },
  { texture: "flare3.dds", position: 1.0, width: 250, colour: [1, 0.6, 0.3, 0.4] },
];

export interface FlareView {
  readonly viewProj: Mat4;
  /** `CCamera::GetTarget` (`LensFlare.cpp:131`), in world millimetres. */
  readonly targetMm: readonly [number, number, number];
  /** The eye, for the camera's own facing: `ms_matView._13/_23/_33` is the
   *  view basis' z axis, which a right-handed look-at makes `eye - target`
   *  (`LensFlare.cpp:152-155`). */
  readonly eyeMm: readonly [number, number, number];
  readonly widthPx: number;
  readonly heightPx: number;
}

export interface FlareState {
  /** `m_bFlareVisible` (`LensFlare.cpp:163-166`). */
  readonly visible: boolean;
  /** `m_afFlareWinPos` (`:386-387`): window pixels, y down. */
  readonly windowX: number;
  readonly windowY: number;
  /** `m_fBeforeBright` and `m_fAfterBright`, already clamped and scaled by the
   *  record's `MaxBrightness` (`:398-407`, `:468-484`). */
  readonly beforeBright: number;
  readonly afterBright: number;
}

export const FLARE_HIDDEN: FlareState = {
  visible: false, windowX: 0, windowY: 0, beforeBright: 0, afterBright: 0,
};

/** `CLensFlare::Interpolate` (`LensFlare.cpp:119-122`). */
export function interpolate(start: number, end: number, percent: number): number {
  return start + (end - start) * percent;
}

/** `ClampBrightness` (`LensFlare.cpp:468-484`): each half into 0..1, then both
 *  scaled by the record's ceiling. */
export function clampBrightness(
  value: number,
  maxBrightness: number,
): number {
  const held = value < 0 ? 0 : value > 1 ? 1 : value;
  return held * maxBrightness;
}

/** `AdjustBrightness` (`LensFlare.cpp:423-439`): the fraction of the
 *  `15 x 15` depth samples that changed, square-rooted, times 0.85, taken off
 *  the after-brightness. `ReadDepthPixels` writes no samples at all
 *  (`:445-463`, its body is commented out), so the original's own occluder
 *  count is always zero and the flare never dims behind a building. */
export function adjustBrightness(afterBright: number, changedSamples: number): number {
  const total = DEPTH_TEST_DIMENSION * DEPTH_TEST_DIMENSION;
  const adjust = Math.sqrt(changedSamples / total) * 0.85;
  return afterBright * (1 - adjust);
}

/** `CLensFlare::Compute` (`LensFlare.cpp:127-177`) with `SetFlareLocation` and
 *  `SetBrightnesses` folded in, because both are pure writes of what it works
 *  out (`:382-392`, `:398-407`).
 *
 * The original tests `acosf(dot) < 0.5 * PI`, which is `dot > 0` for every
 * value in the function's domain and NaN -- so, invisible -- for a dot product
 * that floating point pushed past 1, the case of looking exactly at the sun.
 * The clamp is that bug fixed.
 */
export function computeFlare(
  view: FlareView,
  lightDirection: readonly [number, number, number],
  maxBrightness: number,
): FlareState {
  const [tx, ty, tz] = view.targetMm;
  const sunX = tx - lightDirection[0] * SUN_DISTANCE;
  const sunY = ty - lightDirection[1] * SUN_DISTANCE;
  const sunZ = tz - lightDirection[2] * SUN_DISTANCE;

  const m = view.viewProj;
  const clipX = m[0] * sunX + m[4] * sunY + m[8] * sunZ + m[12];
  const clipY = m[1] * sunX + m[5] * sunY + m[9] * sunZ + m[13];
  const clipW = m[3] * sunX + m[7] * sunY + m[11] * sunZ + m[15];
  // `D3DXVec3Project`'s viewport step (`GrpScreen.cpp:749-757`), with the
  // viewport's own origin at 0: y is flipped, so this is window pixels.
  const w = clipW === 0 ? 1 : clipW;
  const windowX = (clipX / w * 0.5 + 0.5) * view.widthPx;
  const windowY = (0.5 - clipY / w * 0.5) * view.heightPx;

  const magnitude = Math.hypot(sunX, sunY, sunZ) || 1;
  const sunVectorX = -sunX / magnitude;
  const sunVectorY = -sunY / magnitude;
  const sunVectorZ = -sunZ / magnitude;

  let backX = view.eyeMm[0] - tx;
  let backY = view.eyeMm[1] - ty;
  let backZ = view.eyeMm[2] - tz;
  const backLength = Math.hypot(backX, backY, backZ) || 1;
  backX /= backLength;
  backY /= backLength;
  backZ /= backLength;

  const dot = sunVectorX * backX + sunVectorY * backY + sunVectorZ * backZ;
  const clamped = dot < -1 ? -1 : dot > 1 ? 1 : dot;
  const visible = Math.acos(clamped) < 0.5 * Math.PI;

  // The distance from the screen centre, in screen fractions (`:169-174`).
  const fx = windowX / view.widthPx;
  const fy = windowY / view.heightPx;
  const distance = Math.hypot(0.5 - fx, 0.5 - fy);
  return {
    visible,
    windowX,
    windowY,
    beforeBright: clampBrightness(
      interpolate(0, HALF_MAX_BRIGHT, 1 - distance * DISTANCE_SCALE), maxBrightness,
    ),
    afterBright: clampBrightness(
      interpolate(0, 1, 1 - distance * DISTANCE_SCALE), maxBrightness,
    ),
  };
}

/** A screen-space rectangle in window pixels, y down, with the colour its
 *  texture is modulated by. */
export interface FlareQuad {
  readonly texture: string;
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  readonly colour: readonly [number, number, number, number];
}

/** `CLensFlare::DrawBeforeFlare`'s one quad (`LensFlare.cpp:182-263`): a square
 *  `2 * m_fSunSize` wide in the 0..1 ortho and `2 * m_fSunSize * aspect` tall,
 *  which is the same number of pixels in both axes, centred on the flare. */
export function mainFlareQuad(
  texture: string,
  sunSize: number,
  state: FlareState,
  widthPx: number,
  heightPx: number,
): FlareQuad {
  const halfWidth = sunSize * widthPx;
  // `fHeight = m_fSunSize * fAspectRatio` in normalized-y units (`:221-222`),
  // which is `sunSize * widthPx` once multiplied back out by the height.
  const halfHeight = heightPx > 0 ? sunSize * (widthPx / heightPx) * heightPx : 0;
  return {
    texture,
    left: state.windowX - halfWidth,
    top: state.windowY - halfHeight,
    right: state.windowX + halfWidth,
    bottom: state.windowY + halfHeight,
    colour: [1, 1, 1, 1],
  };
}

/** `CFlare::Draw` (`LensFlare.cpp:535-599`): each piece laid on the line from
 *  the flare through the screen centre and out the other side, its colour
 *  scaled by the after-brightness. */
export function flareQuads(
  state: FlareState,
  widthPx: number,
  heightPx: number,
): readonly FlareQuad[] {
  const brightScale = state.afterBright;
  const dx = state.windowX - widthPx / 2;
  const dy = state.windowY - heightPx / 2;
  return FLARE_PIECES.map((piece) => {
    const centreX = state.windowX - (piece.position + 1) * dx;
    const centreY = state.windowY - (piece.position + 1) * dy;
    return {
      texture: piece.texture,
      left: centreX - piece.width,
      top: centreY - piece.width,
      right: centreX + piece.width,
      bottom: centreY + piece.width,
      colour: [
        piece.colour[0] * brightScale,
        piece.colour[1] * brightScale,
        piece.colour[2] * brightScale,
        piece.colour[3] * brightScale,
      ] as const,
    };
  });
}

/** `SetMainFlare`'s resource name (`LensFlare.cpp:296-308`), as a URL. Null
 *  when the record enables no main flare or names no file. */
export function mainFlareTextureUrl(environment: MapEnvironment): string | null {
  const flare = environment.lensFlare;
  if (!flare.enable || !flare.mainFlareEnable) return null;
  const named = flare.mainFlareTextureFileName;
  if (named === null || named.trim() === "") return null;
  const base = named.replace(/\\/g, "/").split("/").pop();
  if (base === undefined || base === "") return null;
  return `${ENVIRONMENT_DIR}${base.toLowerCase()}`;
}

/** Every distinct sprite `CFlare::Init` loads (`LensFlare.cpp:509-530`). */
export function flareTextureUrls(): readonly string[] {
  return [...new Set(FLARE_PIECES.map((piece) => piece.texture))]
    .map((name) => `${ENVIRONMENT_DIR}${name}`);
}

async function fetchImage(url: string): Promise<RgbaImage | null> {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    return decodeImage(await response.arrayBuffer());
  } catch {
    return null;
  }
}

const VERTEX_SOURCE = `#version 300 es
precision highp float;

uniform vec4 u_rect;

out vec2 v_uv;

void main() {
  // The four-vertex strip DrawPrimitiveUP is handed, expanded into two
  // triangles: 0,1,2 then 2,1,3 of the unit quad.
  int order[6] = int[6](0, 1, 2, 2, 1, 3);
  int id = order[gl_VertexID];
  vec2 corner = vec2(float(id & 1), float((id >> 1) & 1));
  // u_rect is (left, bottomNdc, right, topNdc): y grows up in clip space and
  // down in the window, so v runs the other way from the corner.
  v_uv = vec2(corner.x, 1.0 - corner.y);
  gl_Position = vec4(mix(u_rect.xy, u_rect.zw, corner), 0.0, 1.0);
}
`;

const FRAGMENT_SOURCE = `#version 300 es
precision highp float;

in vec2 v_uv;

uniform sampler2D u_sprite;
uniform vec4 u_colour;
uniform float u_textured;

out vec4 fragColour;

void main() {
  // D3DTOP_MODULATE on both the colour and the alpha stage, ARG1 = TEXTURE,
  // ARG2 = DIFFUSE (LensFlare.cpp:545-550). An untextured draw is the
  // DrawAfterFlare bar, which has no texture stage at all (:283-290).
  vec4 texel = mix(vec4(1.0), texture(u_sprite, v_uv), u_textured);
  fragColour = texel * u_colour;
}
`;

const UNIFORMS = ["u_rect", "u_sprite", "u_colour", "u_textured"] as const;

/** One frame's worth of the original's two entry points, holding the state
 *  `CLensFlare` keeps between them. */
export class LensFlare {
  private readonly program: WebGLProgram;
  private readonly uniforms: Uniforms<(typeof UNIFORMS)[number]>;
  private readonly vao: WebGLVertexArrayObject;
  private readonly sprites = new Map<string, WebGLTexture>();
  private environment: MapEnvironment | null = null;
  /** `m_bEnabled` / `m_bShowMainFlare` / `m_fMaxBrightness` / `m_afColor`,
   *  written by `CharacterizeFlare` (`LensFlare.cpp:359-368`). */
  private enabled = false;
  private showMainFlare = false;
  private maxBrightness = 1;
  private colour: readonly [number, number, number] = [1, 1, 1];
  private mainFlareUrl: string | null = null;
  private state: FlareState = FLARE_HIDDEN;
  /** What `setEnvironment` last started; a test awaits it. */
  ready: Promise<void> = Promise.resolve();

  constructor(private readonly gl: WebGL2RenderingContext) {
    this.program = createProgram(gl, VERTEX_SOURCE, FRAGMENT_SOURCE, "lensflare");
    this.uniforms = uniformLocations(gl, this.program, UNIFORMS);
    this.vao = createVertexArray(gl, "lensflare");
  }

  /** `CMapOutdoor::SetEnvironmentLensFlare` (`MapOutdoor.cpp:325-340`):
   *  characterise, then `Initialize` and `SetMainFlare` -- both of which do
   *  nothing at all while the record has the flare disabled. */
  setEnvironment(environment: MapEnvironment): void {
    this.environment = environment;
    const record = environment.lensFlare;
    this.enabled = record.enable;
    this.showMainFlare = record.mainFlareEnable;
    this.maxBrightness = record.maxBrightness;
    this.colour = [
      record.brightnessColour[0], record.brightnessColour[1], record.brightnessColour[2],
    ];
    this.state = FLARE_HIDDEN;
    this.mainFlareUrl = mainFlareTextureUrl(environment);
    if (!this.enabled) {
      this.ready = Promise.resolve();
      return;
    }
    const wanted = [...flareTextureUrls()];
    if (this.mainFlareUrl !== null) wanted.push(this.mainFlareUrl);
    this.ready = this.load(wanted);
  }

  private async load(urls: readonly string[]): Promise<void> {
    const missing = urls.filter((url) => !this.sprites.has(url));
    const images = await Promise.all(missing.map(fetchImage));
    const gl = this.gl;
    for (const [at, image] of images.entries()) {
      if (image === null) continue;
      const texture = gl.createTexture();
      if (texture === null) continue;
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texImage2D(
        gl.TEXTURE_2D, 0, gl.RGBA, image.width, image.height, 0,
        gl.RGBA, gl.UNSIGNED_BYTE, image.pixels,
      );
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.bindTexture(gl.TEXTURE_2D, null);
      this.sprites.set(missing[at]!, texture);
    }
  }

  /** What the last `drawBeforeFlare` worked out. Read by a check, not by a
   *  frame. */
  get flare(): FlareState {
    return this.state;
  }

  /** `CMapOutdoor::RenderBeforeLensFlare` (`MapOutdoorRender.cpp:190-201`):
   *  the main flare is drawn from the *previous* frame's computation, and only
   *  then is this frame's worked out. */
  drawBeforeFlare(view: FlareView): void {
    if (this.enabled && this.showMainFlare && this.state.visible) {
      const url = this.mainFlareUrl;
      const sprite = url === null ? undefined : this.sprites.get(url);
      if (sprite !== undefined && this.environment !== null) {
        this.begin();
        // SRCALPHA / INVSRCALPHA, no depth (`LensFlare.cpp:199-207`).
        this.gl.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA);
        this.paint(
          sprite,
          mainFlareQuad(
            url!, this.environment.lensFlare.mainFlareSize, this.state,
            view.widthPx, view.heightPx,
          ),
          view,
        );
        this.end();
      }
    }
    if (this.environment === null) return;
    this.state = computeFlare(
      view, this.environment.directionalLight.direction, this.maxBrightness,
    );
  }

  /** `CMapOutdoor::RenderAfterLensFlare` (`MapOutdoorRender.cpp:203-208`):
   *  `AdjustBrightness`, then the wash and the eight pieces. */
  drawAfterFlare(view: FlareView): void {
    if (!this.enabled) return;
    // Nothing samples the depth buffer, exactly as nothing does in the
    // original (`LensFlare.cpp:445-463`), so no occluder ever dims this.
    this.state = { ...this.state, afterBright: adjustBrightness(this.state.afterBright, 0) };
    if (!this.state.visible || this.state.afterBright === 0) return;

    this.begin();
    // `DrawAfterFlare` fills `RenderBar2d(0, 0, 1024, 1024)` in a viewport-sized
    // ortho (`LensFlare.cpp:283-290`, `:325-328`), which misses everything past
    // 1024 px on a wider or taller screen. The wash covers the frame instead.
    this.gl.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE_MINUS_SRC_ALPHA);
    this.paint(null, {
      texture: "",
      left: 0, top: 0, right: view.widthPx, bottom: view.heightPx,
      colour: [this.colour[0], this.colour[1], this.colour[2], this.state.afterBright],
    }, view);

    // `CFlare::Draw` saves DESTBLEND = ONE for the pieces (`:537`), so they add.
    this.gl.blendFunc(this.gl.SRC_ALPHA, this.gl.ONE);
    for (const quad of flareQuads(this.state, view.widthPx, view.heightPx)) {
      const sprite = this.sprites.get(`${ENVIRONMENT_DIR}${quad.texture}`);
      if (sprite === undefined) continue;
      this.paint(sprite, quad, view);
    }
    this.end();
  }

  private begin(): void {
    const gl = this.gl;
    gl.useProgram(this.program);
    gl.bindVertexArray(this.vao);
    gl.disable(gl.DEPTH_TEST);
    gl.depthMask(false);
    gl.enable(gl.BLEND);
    gl.blendEquation(gl.FUNC_ADD);
  }

  private end(): void {
    const gl = this.gl;
    gl.disable(gl.BLEND);
    gl.bindVertexArray(null);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.depthMask(true);
  }

  private paint(sprite: WebGLTexture | null, quad: FlareQuad, view: FlareView): void {
    const gl = this.gl;
    const toNdcX = (px: number): number => (px / view.widthPx) * 2 - 1;
    const toNdcY = (px: number): number => 1 - (px / view.heightPx) * 2;
    gl.uniform4f(
      this.uniforms.u_rect,
      toNdcX(quad.left), toNdcY(quad.bottom), toNdcX(quad.right), toNdcY(quad.top),
    );
    gl.uniform4f(
      this.uniforms.u_colour,
      quad.colour[0], quad.colour[1], quad.colour[2], quad.colour[3],
    );
    gl.uniform1f(this.uniforms.u_textured, sprite === null ? 0 : 1);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, sprite);
    gl.uniform1i(this.uniforms.u_sprite, 0);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }
}
